import {LogoutOutlined, UserOutlined} from "@ant-design/icons";
import {Avatar, Dropdown, Menu, notification, Spin} from "@p4ma/p4md-react";
import classNames from "classnames";
import React, {FC} from "react";
import {useTranslation} from "react-i18next";
import {useAuthorization} from "../../api/useAuthorization";
import tryParseApiError from "../../../../utils/tryParseApiError";
import {IMPERSONATION_FEATURE_TOGGLE} from "../../../../utils/impersonation";
import s from "./UserSessionMenu.module.css";

const UserSessionMenu: FC = () => {
  const {loading, signOut, user, endImpersonation, isImpersonating} = useAuthorization();
  const {t} = useTranslation("common");

  const handleEndImpersonation = async () => {
    try {
      notification.info({
        key: "endImpersonation",
        message: t("impersonate.end.loading.message"),
        description: t("impersonate.end.loading.description"),
        duration: 0
      });
      await endImpersonation();
    } catch (error) {
      const description = await tryParseApiError(error);
      notification.error({
        key: "endImpersonationError",
        message: t("impersonate.end.error"),
        description: description ?? undefined
      });
    } finally {
      notification.close("endImpersonation");
    }
  };

  const currentFullName = [user?.lastName, user?.firstName, user?.middleName]
    .filter(part => part && part !== "-")
    .join(" ");

  const currentUserName = user?.userName && user.userName !== "-" ? user.userName : "";
  const impersonatedByUserName = isImpersonating &&
    user?.impersonatingUserLogin ?
    user.impersonatingUserLogin :
    undefined;
  const buttonDisabled = loading.endImpersonation || loading.impersonateUser || loading.signOut;

  const menu = (
    <Menu>
      {isImpersonating ? (
        <>
          <div className={s.impersonation}>
            <UserOutlined
              className={classNames(s.impersonation_icon, s.impersonation_icon_highlighted)}
            />
            <div className={s.impersonation_text}>
              {t("impersonate.session.workingOnBehalfOf")}{" "}
              <span
                data-testid="user-session-menu__working-on-behalf-of-login"
                className={s.impersonation_username}
              >
                {currentUserName}
              </span>
            </div>
            <span aria-hidden="true" />
            <div className={s.impersonation_text}>
              {t("impersonate.session.loggedInAs")}{" "}
              <span
                data-testid="user-session-menu__logged-in-as-login"
                className={s.impersonation_username}
              >
                {impersonatedByUserName}
              </span>
            </div>
          </div>
          {IMPERSONATION_FEATURE_TOGGLE.endImpersonation ? (
            <Menu.Item
              key="endImpersonation"
              icon={loading.endImpersonation ? <Spin size="small" /> : <LogoutOutlined />}
              onClick={handleEndImpersonation}
              disabled={buttonDisabled}
            >
              {t("impersonate.end.action")}
            </Menu.Item>
          ) : null}
        </>
      ) : null}
      <Menu.Item
        className={classNames({
          [s.menu_item_border_top]: isImpersonating
        })}
        key="logout"
        onClick={signOut}
        danger
        disabled={buttonDisabled}
        icon={loading.signOut ? <Spin size="small" /> : <LogoutOutlined />}
      >
        {t("logout")}
      </Menu.Item>
    </Menu>
  );

  return (
    <div className={s.header_user} data-testid="user-session-menu">
      <div className={s.header_user_info}>
        <span className={s.header_user_info_fullName}>{currentFullName}</span>
        <span className={s.header_user_info_userName}>{currentUserName}</span>
      </div>
      <Dropdown overlay={menu} trigger={["click"]}>
        <div
          className={classNames(s.header_avatar, {
            [s.header_avatar_impersonating]: isImpersonating
          })}
          data-testid="user-session-menu__avatar"
        >
          <Avatar shape="circle" size={40} icon={<UserOutlined />} />
        </div>
      </Dropdown>
    </div>
  );
};

export default UserSessionMenu;
